import React from 'react'
import PropTypes from 'prop-types'

// Components
import {
    Box,
    Flex,
    Heading,
    Modal,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalOverlay
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {CartDetails} from '@app/hooks/use-add-to-cart-modal'
import {CloseIcon} from '@app/components/custom-icons'
import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'
import GiftCertificateItem from '@app/components/gift-certificate-item'

// Hooks
import {useBreakpointValue} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useIntl} from 'react-intl'

/**
 * Renders a modal that displays the item added to the cart.
 * @param {Object} params - The parameters for the modal.
 * @param {string|React.ReactNode} params.heading - The heading of the modal.
 * @param {React.ReactNode} params.itemDetails - The item details to display on the left side of the modal.
 * @param {React.ReactNode} params.actionItems - The action items to display on the right side of the modal.
 * @param {boolean} params.isOpen - Indicates whether the modal is open.
 * @param {function} params.onClose - The function to call when the modal is closed.
 * @returns {JSX.Element} The rendered modal component.
 */
export const AddToCartModal = ({heading, itemDetails, actionItems, isOpen, onClose}) => {
    const size = useBreakpointValue({base: 'full', lg: '2xl', xl: '4xl'})

    return (
        <Modal size={size} isOpen={isOpen} onClose={onClose} scrollBehavior="inside" isCentered>
            <ModalOverlay />
            <ModalContent
                containerProps={{'data-testid': 'add-to-cart-modal'}}
                p={[16, null, null, 24]}
            >
                <ModalHeader>
                    <Heading as="h1" fontSize="3xl" textTransform={'uppercase'}>
                        {heading}
                    </Heading>
                </ModalHeader>
                <ModalCloseButton>
                    <CloseIcon boxSize="14px" />
                </ModalCloseButton>

                <ModalBody pt="32px" pb={['166px', null, null, 0]}>
                    <Flex
                        flexDirection={{base: 'column', lg: 'row'}}
                        justifyContent="space-between"
                        paddingBottom={{base: '0', lg: '8'}}
                    >
                        <Box flex="1 0 66%" pr={{lg: '16', xl: '32'}}>
                            {itemDetails}
                        </Box>

                        <HideOnMobile
                            flex="1 0 33%"
                            paddingX={{lg: '0', xl: '0'}}
                            paddingY={{base: '4', lg: '0'}}
                        >
                            {actionItems}
                        </HideOnMobile>
                    </Flex>
                </ModalBody>

                <HideOnDesktop>
                    <ModalFooter
                        position="fixed"
                        display="block"
                        bg="white"
                        width="100%"
                        p={16}
                        left={0}
                        bottom={0}
                        zIndex={1}
                    >
                        {actionItems}
                    </ModalFooter>
                </HideOnDesktop>
            </ModalContent>
        </Modal>
    )
}

AddToCartModal.propTypes = {
    heading: PropTypes.string,
    itemDetails: PropTypes.node,
    actionItems: PropTypes.node,
    isOpen: PropTypes.bool,
    onClose: PropTypes.func
}

export const AddToCartModalGiftcard = ({giftCertificate, isOpen, onClose}) => {
    const intl = useIntl()

    return (
        <AddToCartModal
            isOpen={isOpen}
            onClose={onClose}
            heading={intl.formatMessage({
                defaultMessage: 'Online Gift Card added to the cart',
                id: 'giftcard.added_to_cart_modal.title'
            })}
            itemDetails={
                <GiftCertificateItem
                    giftCertificateItem={giftCertificate}
                    imageStyles={{width: '147px'}}
                    headingStyles={{fontSize: '3xl'}}
                />
            }
            actionItems={<CartDetails />}
        />
    )
}

AddToCartModalGiftcard.propTypes = {
    giftCertificate: PropTypes.object,
    isOpen: PropTypes.bool,
    onClose: PropTypes.func
}
